import { Spin } from 'antd';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { CutoutModel } from '@common/models';
import { SectionSubtitle } from '@core/components';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    addSelectedCutout,
    removeSelectedCutout,
    selectSelectedCutouts,
    updateSelectedCutouts,
} from '@redux/slices/cutoutSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import CutoutItem from './components/CutoutItem';

import './CutoutsSection.scss';

const Cutouts = (): JSX.Element => {
    const { selectedValue: selectedStore } = useSelectedStore();
    const { t } = useTranslation();

    const { cutouts, isFetchingOptionalItems } = useOptionalItems();

    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const selectedCutouts = useAppSelector(selectSelectedCutouts);

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const dispatch = useAppDispatch();

    const handleCutoutItemClick = (cutout: CutoutModel) => {
        const currentSelectedCutout = selectedCutouts.get(cutout.productId);
        if (currentSelectedCutout) {
            if (currentSelectedCutout.isManuallyChanged) {
                // TODO(Alyson) This basically handle the fucntionality of deselecting a
                // cutout item after changing its value. The value must be remembered.
                // We can improve the way this is done only adding to selectedCutouts
                // the cutouts that are REALLY selected.
                const selectedCutoutsClone = new Map(selectedCutouts);
                selectedCutoutsClone.set(cutout.productId, {
                    quantity: currentSelectedCutout.quantity === 0 ? 1 : currentSelectedCutout.quantity,
                    isSelected: !currentSelectedCutout.isSelected,
                    isManuallyChanged: currentSelectedCutout.isManuallyChanged,
                });
                dispatch(updateSelectedCutouts(selectedCutoutsClone));
            } else {
                dispatch(removeSelectedCutout(cutout));
            }
        } else {
            dispatch(addSelectedCutout(cutout));
        }
    };

    const updateQuantity = (cutOut: CutoutModel, quantity: number) => {
        if (quantity < 0) {
            return;
        }

        const newSelectedCutoutsMap = new Map(selectedCutouts);
        const cutout = newSelectedCutoutsMap.get(cutOut.productId);

        if (!cutout) {
            return;
        }

        newSelectedCutoutsMap.set(cutOut.productId, {
            quantity,
            isSelected: cutout.isSelected && quantity > 0,
            isManuallyChanged: true,
        });
        dispatch(updateSelectedCutouts(newSelectedCutoutsMap));
    };

    const changeQuantity = (cutOut: CutoutModel, quantity: number): void => {
        const newSelectedCutoutsMap = new Map(selectedCutouts);
        const cutout = newSelectedCutoutsMap.get(cutOut.productId);

        if (!cutout) {
            return;
        }

        const updatedQuantity = cutout.quantity + quantity;
        if (updatedQuantity < 0) {
            return;
        }

        newSelectedCutoutsMap.set(cutOut.productId, {
            quantity: updatedQuantity,
            isSelected: cutout.isSelected && updatedQuantity > 0,
            isManuallyChanged: true,
        });
        dispatch(updateSelectedCutouts(newSelectedCutoutsMap));
    };

    const areCutoutsAvailable =
        zipcode && selectedStore && selectedMaterial && areThereShapesInTheDrawingArea && cutouts.length;

    if (!areCutoutsAvailable) {
        return <></>;
    }

    return (
        <>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.CUTOUTS.TITLE')}
                description={t('SCROLLABLE_AREA.CUTOUTS.DESCRIPTION')}
                showLine
            />
            {isFetchingOptionalItems ? (
                <Spin style={{ fontSize: 24 }} />
            ) : (
                <div className="card-with-quantity card-with-quantity--large">
                    {cutouts.map((cutout, id) => (
                        <CutoutItem
                            key={id}
                            cutout={cutout}
                            isSelected={selectedCutouts.get(cutout.productId)?.isSelected}
                            quantity={selectedCutouts.get(cutout.productId)?.quantity}
                            updateQuantity={updateQuantity}
                            changeQuantity={changeQuantity}
                            onClick={() => handleCutoutItemClick(cutout)}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export default Cutouts;
